import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useMemo } from 'react';
import MiddleEllipsis from 'react-middle-ellipsis';
import type { ReportStatus, DownloadCategory } from '@/graphql/types';
import Pagination from '../../table/Pagination';
import Table, { type TableProps } from '../../table/Table';
import { BasePage } from '../BasePage';
import useGraphReportQueryGenerator from './utils';
import Button from '../../form/Button';
import { format, formatFileSize } from '../../../utils/formatting';
import { DownloadCategoryTranslations, DownloadStatusTranslations } from './enums';
import { useOrderAndPaginationControl } from '../../table/hooks';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import { EmptyState } from '../../emptyState/EmptyState';

function formatLastDownloaded(date: string): string {
    return format(date, {
        useBrowserTimezone: true,
        hideTime: true,
        dateFormat: 'dd MMM yyyy',
    });
}

type GraphReportEdge = ReturnType<typeof useGraphReportQueryGenerator>['graphReports'][number];
type GraphReport = GraphReportEdge['node'];
type DownloadTableColumn = TableProps<GraphReport>['columns'];
const EMPTY_STATE_VARIANT = 'brand';
const DOWNLOADS_EMPTY_STATE_ICON = faDownload;
const EMPTY_STATE_ACTION_ICON = faUpRightFromSquare;
export const DOWNLOADS_TABLE_SORT = {
    tableId: 'downloads',
    sortableFields: ['name', 'size', 'lastDownloaded'],
} as const;

export default function DownloadsPage(): JSX.Element {
    const orderAndPaginationControl = useOrderAndPaginationControl(DOWNLOADS_TABLE_SORT);
    const { currentPage, pageCount, setCurrentPage, totalCount } = orderAndPaginationControl;

    const { graphReports, updateLastDownloadMutation, loading, updating } =
        useGraphReportQueryGenerator(orderAndPaginationControl);

    const columns = useMemo<DownloadTableColumn>(
        () => [
            {
                key: 'name',
                title: gettext('NAME'),
                render(_column, { id, name }) {
                    return (
                        <div className="max-w-[90%] whitespace-nowrap" title={name}>
                            <MiddleEllipsis key={id}>
                                <span>{name}</span>
                            </MiddleEllipsis>
                        </div>
                    );
                },
            },
            {
                key: 'size',
                title: gettext('SIZE'),
                render(_column, { size }) {
                    return <p>{formatFileSize(size ?? null)}</p>;
                },
            },
            {
                key: 'status',
                title: gettext('STATUS'),
                render(_column, { status }) {
                    return <p>{DownloadStatusTranslations[status as ReportStatus]}</p>;
                },
            },
            {
                key: 'category',
                title: gettext('CATEGORY'),
                render(_column, { category }) {
                    return <p>{DownloadCategoryTranslations[category as DownloadCategory]}</p>;
                },
            },
            {
                key: 'lastDownloaded',
                title: gettext('LAST_DOWNLOAD'),
                render(_column, { lastDownloaded }) {
                    return (
                        <p>
                            {lastDownloaded === null
                                ? gettext('NEVER')
                                : formatLastDownloaded(lastDownloaded as string)}
                        </p>
                    );
                },
            },
            {
                key: '',
                title: (
                    <span className="space-x-2">
                        {updating && <LoadingSpinner className="h-5 align-middle text-gray-400" />}
                        {orderAndPaginationControl.showPagination && (
                            <Pagination orderAndPaginationControl={orderAndPaginationControl} />
                        )}
                    </span>
                ),
                render(_column, { id, url }) {
                    const hasUrl = url !== '';
                    const buttonContent = (
                        <>
                            {gettext('DOWNLOAD')} <FontAwesomeIcon icon={faDownload} />
                        </>
                    );

                    return (
                        <Button
                            variant={hasUrl ? 'downloadButton' : 'downloadButtonSecondary'}
                            disabled={!hasUrl}
                            onClick={() => {
                                void updateLastDownloadMutation({ variables: { id } });
                            }}
                        >
                            {hasUrl ? (
                                <a href={url!} download className="no-underline">
                                    {buttonContent}
                                </a>
                            ) : (
                                buttonContent
                            )}
                        </Button>
                    );
                },
            },
        ],
        [orderAndPaginationControl, updating, updateLastDownloadMutation]
    );

    const title = gettext('DOWNLOAD_PAGE_TITLE');
    const nodes = useMemo(() => graphReports.map((edge) => edge.node), [graphReports]);
    const hasDownloads = totalCount > 0;
    const showTable = loading || hasDownloads;
    const goToGraphsPage = useCallback(() => {
        window.location.assign('/sbr/sbrView/');
    }, []);

    useEffect(() => {
        if (pageCount > 0 && currentPage > pageCount) {
            setCurrentPage(pageCount);
        }
    }, [currentPage, pageCount, setCurrentPage]);

    return (
        <BasePage title={title}>
            {showTable ? (
                <Table
                    data={nodes}
                    columns={columns}
                    header={title}
                    loading={loading}
                    orderAndPaginationControl={orderAndPaginationControl}
                />
            ) : (
                <EmptyState
                    variant={EMPTY_STATE_VARIANT}
                    iconFontAwesome={DOWNLOADS_EMPTY_STATE_ICON}
                    header={gettext('DOWNLOADS_EMPTY_HEADING')}
                    description={gettext('DOWNLOADS_EMPTY_BODY')}
                    actionButtonText={gettext('GO_TO_GRAPHS_PAGE')}
                    actionButtonIconFontAwesome={EMPTY_STATE_ACTION_ICON}
                    actionButtonMethod={goToGraphsPage}
                />
            )}
        </BasePage>
    );
}
